import React from 'react';
import LegalPageLayout, { Section, Paragraph, List, Highlight } from '../../components/LegalPageLayout';

const OwnershipResetPolicy = () => {
    return (
        <LegalPageLayout title="Property Ownership Reset Policy" lastUpdated="March 2026">
            <Section title="Overview">
                <Paragraph>
                    ClientCheck associates job experience reviews with specific addresses. We recognize 
                    that properties change ownership over time, and new owners should not be held 
                    accountable for the job history of previous owners.
                </Paragraph>
                <Paragraph>
                    The Property Ownership Reset system allows address histories to be reset when 
                    ownership changes occur, ensuring fair and accurate experience summaries.
                </Paragraph>
            </Section>

            <Section title="How Ownership Resets Work">
                <Paragraph>
                    When a property ownership reset is processed:
                </Paragraph>
                <List items={[
                    "The address experience score is recalculated excluding pre-reset reviews",
                    "Previous reviews are archived and no longer affect the visible experience summary",
                    "The address profile reflects only reviews submitted after the reset date",
                    "A notation indicates that the address was reset and the date of the reset"
                ]} />
            </Section>

            <Section title="What Happens to Archived Reviews">
                <Paragraph>
                    Reviews archived due to an ownership reset are handled as follows:
                </Paragraph>
                <List items={[
                    "Archived reviews are excluded from public experience score calculations",
                    "They are not displayed on the public address profile",
                    "They may be retained in internal records for moderation and compliance purposes",
                    "They remain part of the submitting contractor's review history"
                ]} />
                <Highlight>
                    Archived reviews are not deleted—they are simply excluded from current 
                    experience summaries to ensure new owners start with a clean slate.
                </Highlight>
            </Section>

            <Section title="Why This System Exists">
                <Paragraph>
                    The ownership reset system serves several important purposes:
                </Paragraph>
                <List items={[
                    "Protects new property owners from inheriting past job history",
                    "Ensures experience summarys reflect current ownership situations",
                    "Maintains fairness when properties change hands",
                    "Supports accurate, relevant information for contractors"
                ]} />
            </Section>

            <Section title="Requesting an Ownership Reset">
                <Paragraph>
                    Ownership resets are processed through our administrative team. Requests 
                    should include:
                </Paragraph>
                <List items={[
                    "The address in question",
                    "Evidence of ownership change (such as sale records)",
                    "The effective date of the ownership change",
                    "Contact information for follow-up"
                ]} />
                <Paragraph>
                    Requests are reviewed by our team and processed at our discretion. We may 
                    request additional documentation to verify ownership changes.
                </Paragraph>
            </Section>

            <Section title="Limitations">
                <Paragraph>
                    The ownership reset system has certain limitations:
                </Paragraph>
                <List items={[
                    "Resets are processed by administrators, not automatically",
                    "We may require documentation to verify ownership changes",
                    "Resets do not remove reviews from contractor histories",
                    "Archived data may be retained for compliance purposes",
                    "We reserve the right to deny reset requests that appear fraudulent"
                ]} />
            </Section>

            <Section title="After a Reset">
                <Paragraph>
                    After an ownership reset is processed:
                </Paragraph>
                <List items={[
                    "The address will show 'Limited Data' until new reviews are submitted",
                    "A notation will indicate the reset date on the address profile",
                    "New reviews will build a fresh history for the address",
                    "The experience score will be calculated solely from post-reset reviews"
                ]} />
            </Section>

            <Section title="Questions">
                <Paragraph>
                    For questions about the ownership reset system or to request a reset, 
                    please contact our support team through the Contact page.
                </Paragraph>
            </Section>
        </LegalPageLayout>
    );
};

export default OwnershipResetPolicy;
